/**
 * InsufficientCreditsDialog - Warning dialog when user lacks credits for an action
 * 
 * Shows:
 * - Current balance vs required credits
 * - Quick purchase options
 * - Cancel/continue options
 */

import { AlertTriangle, Coins, ArrowRight, X } from 'lucide-react';
import type { CreditPack } from '@/hooks/useCredits';

interface InsufficientCreditsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  requiredCredits: number;
  queryType: string;
  packs: CreditPack[];
  onPurchase: (packId: string) => Promise<string | null>;
}

export function InsufficientCreditsDialog({
  isOpen,
  onClose,
  currentBalance,
  requiredCredits,
  queryType,
  packs,
  onPurchase,
}: InsufficientCreditsDialogProps) {
  if (!isOpen) return null;

  const deficit = requiredCredits - currentBalance;
  
  // Find the smallest pack that covers the deficit
  const sortedPacks = [...packs].sort((a, b) => a.credits - b.credits);
  const recommendedPack = sortedPacks.find(p => p.credits >= deficit) || sortedPacks[sortedPacks.length - 1];

  const handleQuickPurchase = async () => {
    if (!recommendedPack) return;
    const checkoutUrl = await onPurchase(recommendedPack.id);
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  // Format query type for display
  const formatQueryType = (type: string) => {
    switch (type) {
      case 'quick_chat': return 'Quick Chat';
      case 'standard_research': return 'Standard Research';
      case 'deep_deliberation': return 'Deep Deliberation';
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative w-full max-w-md bg-surface border border-orange-500/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Warning header */}
        <div className="bg-orange-500/10 p-6 border-b border-orange-500/20">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-orange-500/20 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-text-primary">Insufficient Credits</h2>
              <p className="text-sm text-text-secondary mt-1">
                You need more credits for {formatQueryType(queryType)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-surface-light transition-colors"
            >
              <X className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Balance comparison */}
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-4 bg-surface-light rounded-xl">
            <div className="text-center flex-1">
              <p className="text-xs text-text-muted mb-1">Your Balance</p>
              <p className="text-2xl font-bold text-red-400">{currentBalance.toLocaleString()}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-text-muted mx-4" />
            <div className="text-center flex-1">
              <p className="text-xs text-text-muted mb-1">Up to</p>
              <p className="text-2xl font-bold text-text-primary">{requiredCredits.toLocaleString()}</p>
            </div>
          </div>

          <p className="text-sm text-text-secondary text-center">
            You need <span className="text-orange-400 font-semibold">{deficit.toLocaleString()}</span> more credits
          </p>

          {/* Recommended pack */}
          {recommendedPack && (
            <button
              onClick={handleQuickPurchase}
              className="w-full flex items-center justify-between p-4 bg-verdict-primary/10 border border-verdict-primary/30 rounded-xl hover:bg-verdict-primary/20 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-verdict-primary/20 rounded-lg">
                  <Coins className="w-5 h-5 text-verdict-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-text-primary">{recommendedPack.name}</p>
                  <p className="text-sm text-verdict-primary">
                    {recommendedPack.credits.toLocaleString()} credits
                    {recommendedPack.bonus_percent > 0 && (
                      <span className="ml-2 text-green-400">+{recommendedPack.bonus_percent}% bonus!</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-text-primary">${recommendedPack.price_usd}</p>
                <p className="text-xs text-text-secondary group-hover:text-verdict-primary transition-colors">
                  Quick buy →
                </p>
              </div>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

